import merge from 'deepmerge';
import { PostAuthRoutes } from 'c-routes';
import type { PropsWithChildren } from 'react';
import React, { useContext, useMemo } from 'react';
import { config, EntitySchemaActions } from 'c-data-layer';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import UniledLogo from 'c-assets/uniled-black.svg?react';
import GlobalCss from 'c-wrapper/Components/GlobalCss';
import AppRouter from 'c-wrapper/Components/AppRouter';
import { en, GlobalAppTranslation } from 'c-translation';
import en_us from 'c-translation/Langs/enUS';
import type { ThemeOptions } from '@mui/material/styles';
import {
    createTheme,
    StyledEngineProvider,
    ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { ThemeProvider } from '@emotion/react';
import { useSelector } from 'react-redux';
import { uiSelectors } from 'c-wrapper/Slices/UI';
import SvgLogoWrapper from 'c-wrapper/Components/CommonWrapper/SvgLogoWrapper';
import { LocalizationProvider as LocalizationProviderPro } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import locale from 'date-fns/locale/en-GB';
import { darkThemeOptions, LANG_en_gb, LANG_en_us, skinThemeForMode } from 'c-lib';
import { useColorCorrection, useLanguageSwitcher, useThemeMode } from 'c-hooks';

import { PermissionName } from 'c-sdk';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { authSelectors } from 'c-auth-module/Slices';

type CommonContextValues = {
    postAuthHome?: string;
    components?: {
        BigBrandLogo?: React.ReactNode;
        MiniBrandLogo?: React.ReactNode;
    };
};

const DefaultBigBrand = <SvgLogoWrapper small={false} DefaultLogo={UniledLogo} />;
const DefaultMiniBrand = <SvgLogoWrapper small={false} DefaultLogo={UniledLogo} />;

const CommonContext = React.createContext<CommonContextValues>({
    postAuthHome: PostAuthRoutes.Dashboard.Dashboard,
    components: {
        BigBrandLogo: DefaultBigBrand,
        MiniBrandLogo: DefaultMiniBrand,
    },
});

export const usePostAuthHome = () => {
    const { hasAll } = useUserPermissions();
    // The Dashboard (and its menu entry) needs DashboardReporting; without it, users start on Campaigns
    return hasAll([PermissionName.DashboardReporting])
        ? PostAuthRoutes.Dashboard.Dashboard
        : PostAuthRoutes.Campaigns.CampaignList;
};

export const useComponentOverrides = () => useContext(CommonContext).components;

const AppTranslationResources = {
    en: { translation: en },
    [LANG_en_gb]: { translation: en },
    [LANG_en_us]: { translation: en_us },
};

const CommonWrapper: React.FC<
    PropsWithChildren<{
        themeOptions: ThemeOptions;
        postAuthHome?: string;
        bigLogoOverride?: React.FC;
        smallLogoOverride?: React.FC;
    }>
> = ({
    children,
    postAuthHome = PostAuthRoutes.Dashboard.Dashboard,
    themeOptions,
    bigLogoOverride = UniledLogo,
    smallLogoOverride = UniledLogo,
}) => {
    Object.entries(config.EntitySchemaActions).forEach(([key, actions]) => {
        (EntitySchemaActions as any)[key] = actions;
    });
    const { lang } = useLanguageSwitcher();
    const { daltonizeTheme } = useColorCorrection();
    const { mode, isDarkMode } = useThemeMode();
    const previewTheme = useSelector(uiSelectors.previewTheme);
    const isLoggedIn = useSelector(authSelectors.isLoggedIn);
    const effectivePreviewTheme = useMemo(
        () => (isLoggedIn ? previewTheme : { theme: {} }),
        [isLoggedIn, previewTheme],
    );

    const mergedThemeOptions = useMemo(
        () =>
            merge.all([
                themeOptions,
                isDarkMode ? darkThemeOptions : {},
                skinThemeForMode(effectivePreviewTheme, mode),
            ]),
        [themeOptions, effectivePreviewTheme, isDarkMode, mode],
    );

    const actualTheme = useMemo(
        () => daltonizeTheme(createTheme(mergedThemeOptions)),
        [mergedThemeOptions, daltonizeTheme],
    );

    const actualBigLogo = useMemo(
        () => (
            <SvgLogoWrapper
                small={false}
                dark={isDarkMode}
                DefaultLogo={bigLogoOverride}
                skin={effectivePreviewTheme.skin}
            />
        ),
        [effectivePreviewTheme, bigLogoOverride, isDarkMode],
    );
    const actualSmallLogo = useMemo(
        () => (
            <SvgLogoWrapper
                small
                dark={isDarkMode}
                DefaultLogo={smallLogoOverride}
                skin={effectivePreviewTheme.skin}
            />
        ),
        [effectivePreviewTheme, smallLogoOverride, isDarkMode],
    );

    const CommonContextValue = useMemo<CommonContextValues>(
        () => ({
            postAuthHome,
            components: { MiniBrandLogo: actualSmallLogo, BigBrandLogo: actualBigLogo },
        }),
        [actualBigLogo, actualSmallLogo, postAuthHome],
    );

    return (
        <CommonContext.Provider value={CommonContextValue}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={actualTheme}>
                    <MuiThemeProvider theme={actualTheme}>
                        <LocalizationProviderPro
                            dateAdapter={AdapterDateFns}
                            adapterLocale={locale}
                        >
                            <GlobalCss />
                            <AppRouter>
                                <GlobalAppTranslation
                                    resources={AppTranslationResources}
                                    lng={lang}
                                    fallbackLng="en"
                                />
                                {children}
                            </AppRouter>
                        </LocalizationProviderPro>
                    </MuiThemeProvider>
                </ThemeProvider>
            </StyledEngineProvider>
        </CommonContext.Provider>
    );
};

export default CommonWrapper;
